import { withTranslation } from '@application/i18n/with_translation';
import { UnitNode, UnitService } from '@application/unit.service';
import { Flex, InputCombobox, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { AsyncSignal, AsyncSignalValue, asyncSignalAccessible, asyncSignalIdle } from '@cortex-shim/async_signal.component';
import { RouterService } from '@cortex-shim/router.service';
import { signal } from '@preact/signals-react';
import { Injectable, Scopes, useResolve, useResolveController } from '@visiba/instantiation';
import { safeNumberParse } from '@visiba/std';
import { useViewFindReceptionTempNavigationContext } from './_temp_navigation.context';

export function SelectReception() {
	const controller = useResolveController(Controller);
	const { __TEMP__onNavigate } = useViewFindReceptionTempNavigationContext();
	const unitService = useResolve(UnitService);

	function handleChange(strUnitId: Nullable<string>) {
		if (strUnitId == null) return;
		const unitId = safeNumberParse(strUnitId);
		if (unitId == null) return;

		if (__TEMP__onNavigate) {
			const unit = unitService.getUnitNodeInTree(unitId);
			__TEMP__onNavigate(`/${unit.routeWithoutLicenceholder}`);
			return;
		}

		controller.changeReception(unitId);
	}

	return (
		<Flex gap={tokens.spacing.x2}>
			<Flex gap={tokens.spacing.x1}>
				<Text.h2 variant='headingXSmall'>{withTranslation('View.FindReception.SelectReception.Title')}</Text.h2>
				<Text.p variant='bodyRegular'>{withTranslation('View.FindReception.SelectReception.Description')}</Text.p>
			</Flex>
			<AsyncSignal signal={controller.receptions}>
				{(receptions) => (
					<InputCombobox.Root
						label={withTranslation('View.FindReception.SelectReception.ComboboxLabel')}
						placeholder={withTranslation('View.FindReception.SelectReception.ComboboxPlaceholder')}
						onChange={(unitId) => handleChange(unitId ?? null)}
						multiple={false}
					>
						{receptions.map((reception) => (
							<InputCombobox.Item
								key={reception.id}
								label={reception.name}
								subLabel={reception.subtitle}
								value={reception.id.toString()}
							/>
						))}
					</InputCombobox.Root>
				)}
			</AsyncSignal>
		</Flex>
	);
}

@Injectable({
	scope: Scopes.Scoped,
})
class Controller {
	public receptions = signal<AsyncSignalValue<UnitNode[]>>(asyncSignalIdle());

	constructor(
		private readonly unitService: UnitService,
		private readonly routerService: RouterService,
	) {
		// Empty
	}

	whenMount() {
		this.receptions.value = asyncSignalAccessible(
			[...this.unitService.units.values()].filter((unit) => {
				return unit.isEntrance && unit.isListed;
			}),
		);
	}

	public changeReception(unitId: number) {
		const x = this.unitService.getUnitNodeInTree(unitId);
		this.routerService.navigateByUrl(`/${x.routeWithoutLicenceholder}`);
	}
}
